import {
  Button,
  Card,
  Flex,
  Form,
  Input,
  Select,
  Switch,
  notification,
} from 'antd';
import useForm from 'antd/es/form/hooks/useForm';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import { Server } from '../../../../../../api/server-index';
import { PartnerDto } from '../../../../../../types/dto/partner.dto';
import { PartnerType } from '../../../../../../types/enum/partner-type';
import BackButton from '../../../../../common/buttons/back-button.component';
import Subtitle from '../../../../../common/titles/subtitle.component';
import { formItemLayoutLG } from '../../../../form/form-item.layout';

const NewPartner = () => {
  const navigate = useNavigate();
  const [form] = useForm<PartnerDto>();
  const { t } = useTranslation(['partner', 'common']);
  const [loading, setLoading] = useState<boolean>(false);
  const [showIntl, setShowIntl] = useState<boolean>(false);
  const [partnerToAssign, setPartnerToAssign] = useState<PartnerDto>();

  const fetchPartner = useCallback(async () => {
    const response = await Server.Partner.getPartner();
    if (response) {
      navigate('/partner');
    }
  }, [navigate]);

  useEffect(() => {
    fetchPartner();
  }, [fetchPartner]);

  const createNewPartner = async (values: any) => {
    setLoading(true);
    const response = await Server.Partner.assignPartner(values);
    if (response.status === 200) {
      notification.success({
        message: partnerToAssign ? t('assign.success') : t('new.success'),
      });
      navigate('/partner');
    }
    setLoading(false);
  };

  const getPartnerByPin = async (pin: string) => {
    const response = await Server.Partner.getPartnerByPin(pin);
    if (response) {
      setPartnerToAssign(response);
      if (response.intlBankAccountNr) {
        setShowIntl(true);
      }
      form.setFieldsValue(response);
    }
  };

  return (
    <Card className='mb-2 shadow-sm'>
      <BackButton href={'/partner'} />
      <Subtitle text={t('new.title')} />
      <Form
        name='new-partner'
        className='mt-1'
        form={form}
        onFinish={createNewPartner}
        {...formItemLayoutLG}
      >
        <Form.Item
          label={t('form.pin')}
          className='w-100'
          name='pin'
          rules={[
            { required: true, message: t('form.required') },
            {
              pattern: /^\d{11}$/,
              message: t('form.validate.pin'),
            },
          ]}
          validateTrigger='onBlur'
        >
          <Input
            onChange={(e) => {
              if (e.target.value.length < 11 && partnerToAssign) {
                setPartnerToAssign(undefined);
                form.resetFields();
              }
              if (e.target.value.length === 11) {
                getPartnerByPin(e.target.value);
              }
            }}
          />
        </Form.Item>
        <Form.Item
          label={t('form.name')}
          className='w-100'
          name='name'
          rules={[{ required: true, message: t('form.required') }]}
          validateTrigger='onBlur'
        >
          <Input disabled={partnerToAssign && !partnerToAssign?.editable} />
        </Form.Item>
        <Form.Item
          label={t('form.type')}
          className='w-100'
          name='type'
          rules={[{ required: true, message: t('form.required') }]}
          validateTrigger='onBlur'
        >
          <Select
            disabled={partnerToAssign && !partnerToAssign?.editable}
            options={Object.values(PartnerType).map((type) => {
              return { key: type, value: type, label: t(`types.${type}`) };
            })}
          />
        </Form.Item>
        <Form.Item label={t('form.iban')} name='iban' className='w-100'>
          <Input disabled={partnerToAssign && !partnerToAssign?.editable} />
        </Form.Item>
        <Form.Item label={t('form.bankName')} name='bankName' className='w-100'>
          <Input disabled={partnerToAssign && !partnerToAssign?.editable} />
        </Form.Item>
        <Form.Item label={t('form.address')} name='address' className='w-100'>
          <Input disabled={partnerToAssign && !partnerToAssign?.editable} />
        </Form.Item>
        <Form.Item
          label={t('form.postCode')}
          className='w-100'
          name='postCode'
          rules={[
            {
              pattern: /^\d+$/,
              message: t('form.validate.post_code'),
            },
          ]}
          validateTrigger='onBlur'
        >
          <Input disabled={partnerToAssign && !partnerToAssign?.editable} />
        </Form.Item>
        <Form.Item label={t('form.town')} name='town' className='w-100'>
          <Input disabled={partnerToAssign && !partnerToAssign?.editable} />
        </Form.Item>
        <Form.Item label={t('form.country')} name='country' className='w-100'>
          <Input disabled={partnerToAssign && !partnerToAssign?.editable} />
        </Form.Item>
        <Form.Item label={t('form.phone')} name='phone'>
          <Input disabled={partnerToAssign && !partnerToAssign?.editable} />
        </Form.Item>
        <Form.Item
          label={t('form.email')}
          name='email'
          rules={[{ type: 'email', message: t('form.validate.email') }]}
          validateTrigger='onBlur'
        >
          <Input disabled={partnerToAssign && !partnerToAssign?.editable} />
        </Form.Item>
        <Form.Item label={t('form.website')} name='website'>
          <Input disabled={partnerToAssign && !partnerToAssign?.editable} />
        </Form.Item>
        <Flex align='center' gap={8}>
          <h4>{t('new.title_intl')}</h4>
          <Switch checked={showIntl} onChange={() => setShowIntl(!showIntl)} />
        </Flex>
        {showIntl ? (
          <>
            <Form.Item
              label={t('form.intlBankAccountNr')}
              name='intlBankAccountNr'
            >
              <Input disabled={partnerToAssign && !partnerToAssign?.editable} />
            </Form.Item>
            <Form.Item label={t('form.intlBankSwift')} name='intlBankSwift'>
              <Input disabled={partnerToAssign && !partnerToAssign?.editable} />
            </Form.Item>
            <Form.Item label={t('form.intlBankAdress')} name='intlBankAdress'>
              <Input disabled={partnerToAssign && !partnerToAssign?.editable} />
            </Form.Item>
            <Form.Item label={t('form.intlBankTown')} name='intlBankTown'>
              <Input disabled={partnerToAssign && !partnerToAssign?.editable} />
            </Form.Item>
            <Form.Item label={t('form.intlBankZip')} name='intlBankZip'>
              <Input disabled={partnerToAssign && !partnerToAssign?.editable} />
            </Form.Item>
            <Form.Item label={t('form.intlBankCountry')} name='intlBankCountry'>
              <Input disabled={partnerToAssign && !partnerToAssign?.editable} />
            </Form.Item>
          </>
        ) : (
          <></>
        )}
        <Flex justify='flex-end'>
          <Button loading={loading} htmlType='submit' type='primary' ghost>
            {t('new.submit')}
          </Button>
        </Flex>
      </Form>
    </Card>
  );
};

export default NewPartner;
